import { combineReducers } from 'redux'
import {
  cond,
  always,
  assocPath,
  equals,
  prop,
  T,
  propEq,
  reject,
  prepend,
  drop
} from 'ramda'
import {TYPES} from '../actions/reader'

const setUrlParams = (state = {}, {type, payload}) => cond([
  [equals(TYPES.SET_URL_PARAMS), always(payload)],
  [T, always(state)]
])(type)

const setBookUrl = (state = '', {type, payload}) => cond([
  [equals(TYPES.SET_BOOK_URL), always(payload)],
  [T, always(state)]
])(type)

const setBookId = (state = '', {type, payload}) => cond([
  [equals(TYPES.SET_BOOK_ID), always(payload)],
  [T, always(state)]
])(type)

const setBookReady = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_BOOK_READY), always(payload)],
  [T, always(state)]
])(type)

const setPageReady = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_PAGE_READY), always(payload)],
  [T, always(state)]
])(type)

const setBookInfo = (state = {}, {type, payload}) => cond([
  [equals(TYPES.SET_BOOK_INFO), always(payload)],
  [T, always(state)]
])(type)

const setChapters = (state = {}, {type, payload}) => cond([
  [equals(TYPES.SET_CHAPTERS), always(payload)],
  [T, always(state)]
])(type)

const updateField = (field, state, {payload: {status}}) =>
  assocPath([field], status, state)

const setBookWithMeta = (state = {}, {type, payload}) => cond([
  [equals(TYPES.SET_BOOK_WITH_META), always(payload)],
  [T, always(state)]
])(type)

const setBook = (state = {}, action) => cond([
  [equals(TYPES.SET_BOOK_WITH_META), () => setBookWithMeta(state, action)],
  [equals(TYPES.SET_FAVORITE), () => updateField('favorite', state, action)],
  [T, always(state)]
])(prop('type', action))

const addHighlights = (state = {}, {type, payload}) => cond([
  [equals(TYPES.SET_HIGHLIGHTS), always(payload)],
  [T, always(state)]
])(type)

const deleteHighlightById = (field, state, {payload: { uuid }}) =>
  reject(propEq(field, uuid))(state)

const addHighlightById = (state, {payload}) =>
  prepend(payload, state)

const updateNote = (state, {payload: {note}}) =>
{
  return state.map(h => {
    if (h.uuid === note.uuid) {
      if (note.color) h.color = note.color
      if (note.note) h.note = note.note
    }
    return h
  })
}

const setHighLights = (state = false, action) => cond([
  [equals(TYPES.SET_HIGHLIGHTS), () => addHighlights(state, action)],
  [equals(TYPES.DELETE_HIGHLIGHT), () => deleteHighlightById('uuid', state, action)],
  [equals(TYPES.ADD_HIGHLIGHT), () => addHighlightById(state, action)],
  [equals(TYPES.ON_UPDATE_NOTE), () => updateNote(state, action)],
  [T, always(state)]
])(prop('type', action))

const addBookmarks = (state = {}, {type, payload}) => cond([
  [equals(TYPES.SET_BOOKMARKS), always(payload)],
  [T, always(state)]
])(type)

const deleteBookmarkById = (field, state, {payload: { uuid }}) =>
  reject(propEq(field, uuid))(state)

const addBookmarkById = (state, {payload}) =>
  prepend(payload, state)

const setBookMarks = (state = false, action) => cond([
  [equals(TYPES.SET_BOOKMARKS), () => addBookmarks(state, action)],
  [equals(TYPES.DELETE_BOOKMARK), () => deleteBookmarkById('uuid', state, action)],
  [equals(TYPES.ADD_BOOKMARK), () => addBookmarkById(state, action)],
  [T, always(state)]
])(prop('type', action))

const setPageCount = (state = '', {type, payload}) => cond([
  [equals(TYPES.SET_PAGE_COUNT), always(payload)],
  [T, always(state)]
])(type)

const highlightReducer = (state = false, {type, payload}) => cond([
  [equals(TYPES.HIGHLIGHT_ACTION), always(payload)],
  [T, always(state)]
])(type)

const setDefaultBookFont = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_DEFAULT_BOOK_FONT), always(payload)],
  [T, always(state)]
])(type)

const setBookLoadingProgress = (state = 0, {type, payload}) => cond([
  [equals(TYPES.SET_BOOK_LOADING_PROGRESS), always(payload)],
  [T, always(state)]
])(type)

const setCurrPage = (state = 1, {type, payload}) => cond([
  [equals(TYPES.SET_CURR_PAGE), always(payload)],
  [T, always(state)]
])(type)

const setOpenAddNote = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_OPEN_ADD_NOTE), always(payload)],
  [T, always(state)]
])(type)

const setPageNumPending = (state = null, {type, payload}) => cond([
  [equals(TYPES.SET_PAGE_NUM_PENDING), always(payload)],
  [T, always(state)]
])(type)

const setPageRendering = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_PAGE_RENDERING), always(payload)],
  [T, always(state)]
])(type)

const setFullscreenMode = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_FULLSCREEN_MODE), always(payload)],
  [T, always(state)]
])(type)

const setInBookmarks = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_IN_BOOKMARKS), always(payload)],
  [T, always(state)]
])(type)

const setCurrChapter = (state = '', {type, payload}) => cond([
  [equals(TYPES.SET_CURR_CHAPTER), always(payload)],
  [T, always(state)]
])(type)

const setEditNoteText = (state = '', {type, payload}) => cond([
  [equals(TYPES.SET_EDIT_NOTE_TEXT), always(payload)],
  [T, always(state)]
])(type)

const setSearchResult = (state = [], {type, payload}) => cond([
  [equals(TYPES.SET_SEARCH_RESULT), always(payload)],
  [T, always(state)]
])(type)

const setSelection = (state = {updated: '', selection: ''}, {type, payload}) => cond([
  [equals(TYPES.SET_SELECTION), always(payload)],
  [T, always(state)]
])(type)

const addPrevState = (state, {payload}) =>
  prepend(payload, state)

const removePrevState = (state) =>
  drop(1, state)

const replaceState = (states, {payload: {state, index}}) => {
  states[index] = state
  return states
}

const setPrevStates = (state = [], action) => cond([
  [equals(TYPES.ADD_PREV_STATE), () => addPrevState(state, action)],
  [equals(TYPES.REMOVE_PREV_STATE), () => removePrevState(state)],
  [equals(TYPES.REPLACE_PREV_STATE), () => replaceState((state), action)],
  [T, always(state)]
])(prop('type', action))

const setSelectedNote = (state = {}, {type, payload}) => cond([
  [equals(TYPES.SET_SELECTED_NOTE), always(payload)],
  [T, always(state)]
])(type)

const setSampleMode = (state = false, {type, payload}) => cond([
  [equals(TYPES.SET_SAMPLE_MODE), always(payload)],
  [T, always(state)]
])(type)

export const readerReducer = combineReducers({
  userData: setUrlParams,
  bookUrl: setBookUrl,
  bookId: setBookId,
  bookReady: setBookReady,
  pageReady: setPageReady,
  bookInfo: setBookInfo,
  book: setBook,
  bookmarks: setBookMarks,
  highlights: setHighLights,
  chapters: setChapters,
  pageCount: setPageCount,
  activeHighlight: highlightReducer,
  defaultBookFont: setDefaultBookFont,
  bookLoadingProgress: setBookLoadingProgress,
  currPage: setCurrPage,
  openAddNote: setOpenAddNote,
  pageNumPending: setPageNumPending,
  pageRendering: setPageRendering,
  fullscreen: setFullscreenMode,
  inBookmarks: setInBookmarks,
  editNoteText: setEditNoteText,
  searchResult: setSearchResult,
  currChapter: setCurrChapter,
  selection: setSelection,
  prevStates: setPrevStates,
  selectedNote: setSelectedNote,
  isSampleMode: setSampleMode
})


// WEBPACK FOOTER //
// ./src/reducers/readerReducer.js